import { fromZonedTime, toZonedTime, getTimezoneOffset } from 'date-fns-tz'
import {
  differenceInMinutes,
  format,
  fromUnixTime,
  parse,
  parseISO,
} from 'date-fns'
import { SupportedLocale } from '../constants/locale'
import { DateDisplayFormat } from '../constants/venue'
import { normalizeSupportedLocale } from './localeUtils'

export const formInputTimeFormat = 'HH:mm'
export const formInputDateFormat = 'ISO'

export const zodInputTimeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/

/**
 * @desc ISO 8601 string format
 */
export const zodInputDateRegex =
  /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/

export const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone

export const unixToDateInZone = (unix: number, timezone?: string) => {
  // Convert milliseconds to seconds for fromUnixTime
  return toZonedTime(fromUnixTime(unix / 1000), timezone ?? browserTz)
}

export const diaryDateISOString = (date: Date) => {
  date.setHours(6)
  date = new Date(date.getTime() - 60000 * date.getTimezoneOffset())

  date.setMinutes(0)
  date.setSeconds(0)
  date.setMilliseconds(0)

  return date.toISOString()
}

export const startOfDayDateISOString = (date: Date) => {
  date.setHours(0)
  date = new Date(date.getTime() - 60000 * date.getTimezoneOffset())

  date.setMinutes(0)
  date.setSeconds(0)
  date.setMilliseconds(0)
  return date.toISOString()
}

export const endOfDayDateISOString = (date: Date) => {
  date.setHours(23)
  date = new Date(date.getTime() - 60000 * date.getTimezoneOffset())

  date.setMinutes(59)
  date.setSeconds(59)
  date.setMilliseconds(999)
  return date.toISOString()
}

export const formatUTCDateInZone = ({
  date,
  timezone,
  formatStr,
}: {
  date: Date | string
  timezone: string
  formatStr?: string | 'ISO'
}) => {
  formatStr = formatStr ?? 'h:mma'

  const zoneDate = utcDateToDate(date, timezone)

  if (formatStr === 'ISO') return zoneDate.toISOString()

  return format(zoneDate, formatStr)
}

export const formatUTCDate = ({
  date,
  formatStr,
}: {
  date: Date | string
  formatStr?: string | 'ISO'
}) => {
  formatStr = formatStr ?? 'h:mma'

  //creating a new Date() will shift the date to the current timezone,
  //so subtract the current offset to create a new Date object in the current zone,
  //with the same UTC date
  date = subtractZoneOffset(new Date(date), browserTz)

  if (formatStr === 'ISO') return date.toISOString()

  return format(date, formatStr)
}

const getEnglishOrdinalSuffix = (day: number): string => {
  const modHundred = day % 100
  if (modHundred >= 11 && modHundred <= 13) return 'th'

  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

export const formatEventDisplayDate = ({
  date,
  locale,
}: {
  date: Date | string
  locale?: string | null
}): string => {
  const eventDate = new Date(date)
  if (Number.isNaN(eventDate.getTime())) return ''

  const resolvedLocale = normalizeSupportedLocale(locale)
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }
  const formatter = new Intl.DateTimeFormat(resolvedLocale, options)

  if (resolvedLocale !== SupportedLocale.EN) {
    return formatter.format(eventDate)
  }

  const formattedParts = formatter.formatToParts(eventDate)
  const weekday = formattedParts.find((part) => part.type === 'weekday')?.value
  const month = formattedParts.find((part) => part.type === 'month')?.value
  const dayPart = formattedParts.find((part) => part.type === 'day')?.value
  const dayNumber = dayPart ? Number(dayPart) : Number.NaN

  if (!weekday || !month || Number.isNaN(dayNumber)) {
    return formatter.format(eventDate)
  }

  return `${weekday}, ${month} ${dayNumber}${getEnglishOrdinalSuffix(dayNumber)}`
}

export const getDaysAgo = (date: Date) => {
  //get a string like 45m ago, 1h ago, 1d ago, 1w ago, 1m ago, 1y ago from current date
  const diff = differenceInMinutes(new Date(), date)
  if (diff < 60) return `${diff}m ago`
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`
  if (diff < 10080) return `${Math.floor(diff / 1440)}d ago`
  if (diff < 43200) return `${Math.floor(diff / 10080)}w ago`
  if (diff < 525600) return `${Math.floor(diff / 43200)}m ago`
  return `${Math.floor(diff / 525600)}y ago`
}

export const addZoneOffset = (date: Date, timezone: string) => {
  date = new Date(date)
  date.setMilliseconds(getTimezoneOffset(timezone, date))

  return date
}

export const subtractZoneOffset = (date: Date, timezone: string) => {
  date = new Date(date)
  date.setMilliseconds(getTimezoneOffset(timezone, date) * -1)

  return date
}

export const dateToUTCDate = (date: Date | string, timezone: string) => {
  return fromZonedTime(date, timezone)
}

export const utcDateToDate = (date: Date | string, timezone: string) => {
  return toZonedTime(date, timezone)
}

/**
 * @param {string} time - must be in the format HH:MM - ie 22:30 === 10:30pm
 */
export const parseTimeString = (time: string, date?: Date) => {
  return parse(time, 'H:m', date ?? new Date())
}

/**
 * @param {string} date - must be an ISO 8601 date string
 */
export const parseISOString = (date: string) => {
  return parseISO(date)
}

export const combineDate = (date1: Date, date2: Date) => {
  date1 = new Date(date1.getTime())
  date1.setHours(date2.getHours())
  date1.setMinutes(date2.getMinutes())
  date1.setSeconds(date2.getSeconds())
  date1.setMilliseconds(date2.getMilliseconds())

  return date1
}

export const diaryDate = (date: Date) => {
  date = new Date(date)
  date.setHours(6)
  date.setMinutes(0)
  date.setSeconds(0)
  date.setMilliseconds(0)

  return date
}

export const serverDateStringToFormInputTimeString = (
  date: string,
  timezone: string
) => {
  return formatUTCDateInZone({
    date: date,
    timezone: timezone,
    formatStr: formInputTimeFormat,
  })
}

/**
 * @param {string} date - must be an ISO 8601 date string
 * @desc our date picker (Flatpickr) takes an ISO string and creates a date object, which will
 * shift the date to the current zone. So to offset this shift, we add the zone offset to our UTC date first
 * super weird, but seems to work
 */
export const serverDateStringToFormInputDateString = (
  date: string | Date,
  timezone: string
) => {
  if (!date) return ''

  return formatUTCDateInZone({
    date,
    timezone,
    formatStr: formInputDateFormat,
  })
}

export const dayNumberToName = (day: number) => {
  switch (day) {
    case 0:
      return 'Sun.'
    case 1:
      return 'Mon.'
    case 2:
      return 'Tues.'
    case 3:
      return 'Wed.'
    case 4:
      return 'Thurs.'
    case 5:
      return 'Fri.'
    case 6:
      return 'Sat.'
    default:
      return ''
  }
}

export const diaryDateString = (date: Date) => {
  const diaryDate = new Date(date)
  diaryDate.setHours(6)
  diaryDate.setMinutes(0)
  diaryDate.setSeconds(0)
  diaryDate.setMilliseconds(0)

  // Add the timezone offset to get the correct UTC time for 6AM local
  return addZoneOffset(diaryDate, browserTz).toISOString()
}

export function utcRange(
  start: number | string,
  {
    days = 0,
    months = 0,
    years = 0,
  }: { days?: number | null; months?: number | null; years?: number | null }
) {
  const beginningDate = new Date(start)
  const dateStart = Date.UTC(
    beginningDate.getFullYear(),
    beginningDate.getMonth(),
    beginningDate.getDate(),
    0,
    0,
    0
  )
  const dateEnd = Date.UTC(
    beginningDate.getFullYear() + (years ?? 0),
    beginningDate.getMonth() + (months ?? 0),
    beginningDate.getDate() + (days ?? 0),
    0,
    0,
    0
  )

  return [dateStart, dateEnd]
}

// ----------------- NEW DATE UTILS ----------------- //
// Helpers for "diary day" semantics (6AM boundary). Use these so calendar days
// and API/URL dates stay consistent across timezones instead of mixing
// local 6AM, addZoneOffset, and raw timestamps.

/**
 * Combines a form date string and time string in a timezone, returns UTC ISO string.
 * Use when mapping form date+time fields (e.g. dateStart, dateEnd) to API.
 */
export const formDateTimeToUTCISO = (
  dateStr: string,
  timeStr: string,
  timezone: string
): string => {
  const normalizedDateStr = dateStr.includes('T')
    ? diaryIsoToVenueYmd(dateStr)
    : dateStr
  const [year, month, day] = normalizedDateStr.split('-').map(Number)
  const [hours, minutes] = timeStr.split(':').map(Number)
  const dateTime = new Date(year, month - 1, day, hours, minutes, 0, 0)
  return dateToUTCDate(dateTime, timezone).toISOString()
}

/**
 * Reinterprets a floating-UTC instant (UTC Y/M/D/H/M = intended venue wall-clock)
 * as a true UTC instant in `timezone`. Use at write boundaries for legacy
 * checkout/time-picker values that stored local clock hours as UTC.
 */
export const floatingUtcWallClockToTrueUtcMs = (
  floatingUtc: Date | number | string,
  timezone: string
): number => {
  const input = new Date(floatingUtc)
  if (Number.isNaN(input.valueOf())) {
    throw new Error('Invalid floating UTC date')
  }
  const y = input.getUTCFullYear()
  const month = `${input.getUTCMonth() + 1}`.padStart(2, '0')
  const day = `${input.getUTCDate()}`.padStart(2, '0')
  const hours = `${input.getUTCHours()}`.padStart(2, '0')
  const minutes = `${input.getUTCMinutes()}`.padStart(2, '0')
  return new Date(
    formDateTimeToUTCISO(
      `${y}-${month}-${day}`,
      `${hours}:${minutes}`,
      timezone
    )
  ).getTime()
}

/**
 * Parses a form date string in a timezone, returns UTC ISO string (midnight semantics).
 * Use when mapping form date-only fields (e.g. recurringEndDate, enabledOn) to API.
 */
export const formDateToUTCISO = (dateStr: string, timezone: string): string => {
  const normalizedDateStr = dateStr.includes('T')
    ? diaryIsoToVenueYmd(dateStr)
    : dateStr
  const [year, month, day] = normalizedDateStr.split('-').map(Number)
  const date = new Date(year, month - 1, day, 0, 0, 0, 0)
  return dateToUTCDate(date, timezone).toISOString()
}

/**
 * Returns calendar YYYY-MM-DD from the UTC day in a diary picker ISO value.
 * Diary inputs use a 06:00:00.000Z model, so we must preserve the UTC calendar
 * day to avoid shifting back one day in western timezones.
 */
export const diaryIsoToVenueYmd = (diaryIso: string): string => {
  const date = new Date(diaryIso)
  const year = date.getUTCFullYear()
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0')
  const day = `${date.getUTCDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Returns 6AM on the UTC calendar day of `date` in local time.
 * Use when the date represents a UTC "diary" instant (e.g. 6AM UTC on a selected day)
 * so the selected calendar day is preserved in western timezones.
 */
export const diaryDateFromUTCDay = (date: Date): Date => {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    6,
    0,
    0,
    0
  )
}

/**
 * Returns the UTC timestamp (ms) for 6AM UTC on the calendar day of `date`.
 * Use for API ids (e.g. admissionGroupStats) so the backend receives 6AM UTC
 * and displays show 6AM instead of a local 6AM converted to UTC (e.g. 2PM).
 */
export const diaryDayTimestampUTC = (date: Date): number => {
  return Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    6,
    0,
    0,
    0
  )
}

/**
 * Returns the ISO string for 6AM UTC on the UTC calendar day of `date`.
 * Use for API date range params so the URL and backend always see 06:00:00.000Z
 * regardless of user timezone.
 */
export const diaryDateISOStringUTC = (date: Date): string => {
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      6,
      0,
      0,
      0
    )
  ).toISOString()
}

/**
 * Returns the ISO string for 6AM UTC on the local calendar day of `date`.
 * Use when "today" should mean the user's local date (e.g. default dateStart on Events page).
 */
export const diaryDateISOStringUTCLocalDay = (date: Date): string => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 6, 0, 0, 0)
  ).toISOString()
}

/**
 * Returns the current venue calendar day as a local Date at 6:00 AM.
 * Unlike {@link venueBusinessDate}, this does not apply the nightlife 6AM cutoff.
 */
export const venueCalendarDate = (
  timezone: string,
  now: Date = new Date()
): Date => {
  const dateInVenueZone = utcDateToDate(now, timezone)
  return new Date(
    dateInVenueZone.getFullYear(),
    dateInVenueZone.getMonth(),
    dateInVenueZone.getDate(),
    6,
    0,
    0,
    0
  )
}

/**
 * Returns the current venue business day as a local Date at 6:00 AM.
 * Business day boundary is 6AM in the venue timezone.
 */
export const venueBusinessDate = (
  timezone: string,
  now: Date = new Date()
): Date => {
  const dateInVenueZone = utcDateToDate(now, timezone)
  const adjusted = new Date(dateInVenueZone)

  if (adjusted.getHours() < 6) {
    adjusted.setDate(adjusted.getDate() - 1)
  }

  return new Date(
    adjusted.getFullYear(),
    adjusted.getMonth(),
    adjusted.getDate(),
    6,
    0,
    0,
    0
  )
}

/**
 * Returns 6AM UTC diary ISO string for the current venue calendar day
 * (no nightlife 6AM cutoff).
 */
export const diaryDateISOStringUTCVenueCalendarDay = (
  timezone: string,
  now: Date = new Date()
): string => {
  return diaryDateISOStringUTCLocalDay(venueCalendarDate(timezone, now))
}

/**
 * Returns 6AM UTC diary ISO string for the current venue business day.
 */
export const diaryDateISOStringUTCVenueDay = (
  timezone: string,
  now: Date = new Date()
): string => {
  return diaryDateISOStringUTCLocalDay(venueBusinessDate(timezone, now))
}

/**
 * True when a UTC-diary date ISO (06:00:00.000Z model) is earlier than venue-today.
 */
export const isDiaryDateInPastForVenue = (
  date: string | Date,
  timezone: string,
  now: Date = new Date()
): boolean => {
  const selectedDate = diaryDateFromUTCDay(new Date(date))
  const venueToday = venueBusinessDate(timezone, now)
  return selectedDate.getTime() < venueToday.getTime()
}

/**
 * Resolves selected operational date when an app resumes.
 * Past dates advance to the current venue business day.
 * Today's and future dates are preserved.
 */
export const resolveOperationalDateOnResume = (
  selectedDate: Date | null | undefined,
  timezone: string,
  now: Date = new Date()
): Date => {
  const venueToday = venueBusinessDate(timezone, now)

  if (!selectedDate) {
    return venueToday
  }

  const normalizedSelectedDate = diaryDateFromUTCDay(new Date(selectedDate))

  return isDiaryDateInPastForVenue(normalizedSelectedDate, timezone, now)
    ? venueToday
    : normalizedSelectedDate
}

/**
 * Resolves selected operational date on resume, but only when the selected date
 * represented venue-today at the time the app was hidden.
 */
export const resolveStaleVenueTodayOnResume = (
  selectedDate: Date,
  venueTodayWhenHidden: Date,
  timezone: string,
  now: Date = new Date()
): Date => {
  const normalizedSelectedDate = diaryDateFromUTCDay(new Date(selectedDate))
  const normalizedVenueTodayWhenHidden = diaryDateFromUTCDay(
    new Date(venueTodayWhenHidden)
  )

  if (
    normalizedSelectedDate.getTime() !==
    normalizedVenueTodayWhenHidden.getTime()
  ) {
    return normalizedSelectedDate
  }

  const venueTodayNow = venueBusinessDate(timezone, now)
  return normalizedSelectedDate.getTime() < venueTodayNow.getTime()
    ? venueTodayNow
    : normalizedSelectedDate
}

export const resolveVenueDateDisplayFormat = (
  dateDisplayFormat?: string | null
): DateDisplayFormat => {
  return dateDisplayFormat === DateDisplayFormat.DD_MM_YYYY
    ? DateDisplayFormat.DD_MM_YYYY
    : DateDisplayFormat.MM_DD_YYYY
}

export const getDateFnsDateFormatByVenueDateDisplayFormat = (
  dateDisplayFormat?: string | null
): string => {
  return resolveVenueDateDisplayFormat(dateDisplayFormat) ===
    DateDisplayFormat.DD_MM_YYYY
    ? 'dd/MM/yyyy'
    : 'MM/dd/yyyy'
}

export const getFlatpickrDateFormatByVenueDateDisplayFormat = (
  dateDisplayFormat?: string | null
): string => {
  return resolveVenueDateDisplayFormat(dateDisplayFormat) ===
    DateDisplayFormat.DD_MM_YYYY
    ? 'd/m/Y'
    : 'm/d/Y'
}

export const getDateInputPlaceholderByVenueDateDisplayFormat = (
  dateDisplayFormat?: string | null
): string => {
  return resolveVenueDateDisplayFormat(dateDisplayFormat) ===
    DateDisplayFormat.DD_MM_YYYY
    ? 'DD/MM/YYYY'
    : 'MM/DD/YYYY'
}

export const getDateFnsLongDateFormatByVenueDateDisplayFormat = (
  dateDisplayFormat?: string | null
): string => {
  return resolveVenueDateDisplayFormat(dateDisplayFormat) ===
    DateDisplayFormat.DD_MM_YYYY
    ? 'EEEE, d MMMM yyyy'
    : 'EEEE, MMMM d, yyyy'
}

export const formatLongDateByVenueDateDisplayFormat = (options: {
  date?: Date | string | null
  dateDisplayFormat?: string | null
}): string | null => {
  const { date, dateDisplayFormat } = options
  if (!date) return null

  const parsedDate =
    date instanceof Date
      ? date
      : typeof date === 'string'
        ? new Date(date)
        : null

  if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
    return null
  }

  return format(
    parsedDate,
    getDateFnsLongDateFormatByVenueDateDisplayFormat(dateDisplayFormat)
  )
}

export const getDateFnsMonthDayFormatByVenueDateDisplayFormat = (
  dateDisplayFormat?: string | null
): string => {
  return resolveVenueDateDisplayFormat(dateDisplayFormat) ===
    DateDisplayFormat.DD_MM_YYYY
    ? 'dd/MM'
    : 'MM/dd'
}
